import React, {useState, useEffect, useMemo} from 'react';
import useApiControl from '../../../common/useApiControl';
import RoomUser from '../RoomUser';
import AddFollowRivalDialog from '../../../components/dialogs/AddFollowRivalDialog'
import {useSSEData} from '../../../common/useSseControl';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

function RoomUsers(props) 
{
  const event_id = props.event_id;
  const onReady = props.onReady;
  const maxUsers = Number.isFinite(Number(props.max)) ? Math.max(0, Number(props.max)) : 0;

  const [exceptionOccurred, setExceptionOccurred] = useState(false);
  const [addFollowRivalDialogVisible, setAddFollowRivalDialogVisible] = useState(false);
  const [dialogDatas, setDialogDatas] = useState({
    targetUser: { user_id: null }, // 表示対象ユーザー（初期状態は空ID）
    user: { id: null },            // 自分自身（props.user.id に対応）
  });
  const { sseData } = useSSEData(); // SSEからのデータを取得するフック

  // table表示するuser情報
  const [userDatas, setUserDatas] = useState(null);
  const pushUserDatas = function(pushDatas) {
    if (userDatas) {
      // 現在の all の user_id セットを作成
      const existingIds = new Set(userDatas.all.map(u => u.user_id));
  
      // まだ all に含まれていないユーザーだけを newUsers とする
      const newUsers = pushDatas.filter(u => !existingIds.has(u.user_id));
  
      // newUsers の中で follow 対象と rivals 対象を分類
      const newFollow = newUsers.filter(u => follow.includes(u.user_id));
      const newRivals = newUsers.filter(u => rivals.includes(u.user_id));
  
      if (newUsers.length > 0) {
        setUserDatas({
          ...userDatas,
          follow: [...userDatas.follow, ...newFollow],
          rivals: [...userDatas.rivals, ...newRivals],
          all: [...userDatas.all, ...newUsers]
        });
      }
    }
  }
  
  //フォロー
  const [follow, setFollow] = useState(JSON.parse(props.user.follow));
  const isFollowed = function(id) {
      return follow.includes(id);
  }
  const addFollow = function(id) {
    setFollow([...follow, id]);

    if (userDatas) {
      const newFollowList = [...userDatas.follow];
      const moved = userDatas.all.find(u => u.user_id === id);
      if (moved) {
        newFollowList.push(moved);
        setUserDatas({
          ...userDatas,
          follow: newFollowList,
        });
      }
    }
  }
  const removeFollow = function(id) {
    setFollow(follow.filter(item => item !== id));
  
    if (userDatas) {
      const removed = userDatas.follow.find(u => u.user_id === id);
      if (removed) {
        const newFollowList = userDatas.follow.filter(u => u.user_id !== id);
        setUserDatas({
          ...userDatas,
          follow: newFollowList,
        });
      }
    }
  };
  
  //ライバル
  const [rivals, setRivals] = useState(JSON.parse(props.user.rivals));
  const isRivals = function(id) {
      return rivals.includes(id);
  }
  const addRivals = function(id) {
    setRivals([...rivals, id]);
  
    if (userDatas) {
      const newRivalsList = [...userDatas.rivals];
      const moved = userDatas.all.find(u => u.user_id === id);
      if (moved) {
        newRivalsList.push(moved);
        setUserDatas({
          ...userDatas,
          rivals: newRivalsList,
        });
      }
    }
  }    
  const removeRivals = function(id) {
    setRivals(rivals.filter(item => item !== id));

    if (userDatas) {
      const removed = userDatas.rivals.find(u => u.user_id === id);
      if (removed) {
        const newRivalsList = userDatas.rivals.filter(u => u.user_id !== id);
        setUserDatas({
          ...userDatas,
          rivals: newRivalsList,
        });
      }
    }
  }
  
  const params = useMemo(() => ({
    event_id: event_id,
    max: 20,
  }), [event_id]);
  const { data, executeRequest } = useApiControl({
      action: '/get_event_users',
      params: params,
      method: 'POST',
  });

  // API返却値からユーザー情報から勝率を算出して追加
  useEffect(() => {
    if (data) {
      // ユーザー情報すべて（all, follow, rivals）に winRate を追加
      const calcWinRate = (user) => {
        const wins = user.wins ?? 0;
        const rounds = user.rounds_played ?? 0;

        if (rounds === 0) return 0;

        const rate = (wins / rounds) * 100;
        return Math.round(rate * 10) / 10; // 少数第1位まで、第二位四捨五入
      };

      const addWinRate = (users) =>
        users.map(user => ({
          ...user,
          winRate: calcWinRate(user),
        }));

      setUserDatas({
        all: addWinRate(data.all),
        follow: addWinRate(data.follow),
        rivals: addWinRate(data.rivals),
      });
    }
  }, [data]);

  useEffect(() => {
    if (userDatas) {
      onReady?.();
    }
  }, [userDatas, onReady]);

  useEffect(() => {
      // エラー発生時は再実行しない
      if (exceptionOccurred) return;

      executeRequest().catch((error) => {
          console.error(error);
          setExceptionOccurred(true);
      });
    }, [executeRequest, exceptionOccurred]);

    const roomUserClick = function(targetUser) {
      setDialogDatas({
        targetUser:targetUser,
        user:props.user
      });
      if(props.user.id === targetUser?.user_id) {
          setAddFollowRivalDialogVisible(false);
        } else {
          setAddFollowRivalDialogVisible(true);
        }
    }

  // sse チェック
  useEffect(() => {
    if (
      sseData?.type === 'direct' &&
      sseData.action === 'push' &&
      sseData.target === 'userList' &&
      Array.isArray(sseData.payload)
    ) {
      pushUserDatas(sseData.payload);
    }
  }, [sseData]);


  // エラーが発生した場合はストレージを消してリロード(再ログインさせる)
  if (exceptionOccurred) {
      alert('エラーが発生しました。再ログインしてくださいです。。。')
      // localStorage.removeItem('authToken');
      // localStorage.removeItem('user');
      // localStorage.removeItem('event');
      // localStorage.removeItem('ownersEvent');
      // window.location.reload();
      
  }

  const headerCellStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#444',
    padding: '10px 12px',
    textAlign: 'center',
    lineHeight: '1.6',
    borderBottom: '2px solid #ccc',
    backgroundColor: '#f3f4f6',
  };

  if (userDatas) {
    return (
      <TableContainer component={Paper}
        style={{
          margin: '32px auto',
          borderRadius: '12px',
          boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
          maxWidth: '960px',
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <AddFollowRivalDialog
            isVisible={addFollowRivalDialogVisible}
            onClose={() => setAddFollowRivalDialogVisible(false)}
            dialogDatas={dialogDatas}
            isFollowed={(id)=> isFollowed(id)}
            addFollow={(id)=> addFollow(id)}
            removeFollow={(id)=> removeFollow(id)}
            isRivals={(id)=> isRivals(id)}
            addRivals={(id)=> addRivals(id)}
            removeRivals={(id)=> removeRivals(id)}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ ...headerCellStyle, width: '60px' }}>順位</TableCell>
              <TableCell style={{ ...headerCellStyle, minWidth: '160px' }}>名前</TableCell>
              <TableCell style={{ ...headerCellStyle, width: '90px' }}>収支</TableCell>
              <TableCell style={{ ...headerCellStyle, width: '90px' }}>勝率</TableCell>
              <TableCell style={{ ...headerCellStyle, minWidth: '220px' }}>コメント</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* --- フォローセクション --- */}
            {userDatas.follow.length > 0 && (
              <TableRow>
                <TableCell colSpan={5}
                  style={{
                    backgroundColor: '#e9ecef',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    padding: '10px 16px',
                    color: '#333',
                  }}
                >フォロー中のユーザー
                </TableCell>
              </TableRow>
            )}
            {userDatas.follow.slice(0, maxUsers).map((tableUser, index) => (
              <RoomUser
                user={tableUser}
                index={index}
                max={props.max}
                key={tableUser.user_id}
                isFollow={isFollowed(tableUser.user_id)}
                isRivals={isRivals(tableUser.user_id)}
                onClick={() => roomUserClick(tableUser)}
              />
            ))}

            {/* --- ライバルセクション --- */}
            {userDatas.rivals.length > 0 && (
              <TableRow>
                <TableCell colSpan={5}
                  style={{
                    backgroundColor: '#e9ecef',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    padding: '10px 16px',
                    color: '#333',
                  }}
                >ライバル
                </TableCell>
              </TableRow>
            )}
            {userDatas.rivals.slice(0, maxUsers).map((tableUser, index) => (
              <RoomUser
                user={tableUser}
                index={index}
                max={props.max}
                key={tableUser.user_id}
                isFollow={isFollowed(tableUser.user_id)}
                isRivals={isRivals(tableUser.user_id)}
                onClick={() => roomUserClick(tableUser)}
              />
            ))}

            {/* --- 全ユーザーセクション --- */}
            {userDatas.all.length > 0 && (
              <TableRow>
                <TableCell colSpan={5}
                  style={{
                    backgroundColor: '#e9ecef',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    padding: '10px 16px',
                    color: '#333',
                  }}
                >
                  全ユーザー
                </TableCell>
              </TableRow>
            )}
            {userDatas.all.slice(0, maxUsers).map((tableUser, index) => (
              <RoomUser
                user={tableUser}
                index={index}
                max={props.max}
                key={tableUser.user_id}
                isFollow={isFollowed(tableUser.user_id)}
                isRivals={isRivals(tableUser.user_id)}
                onClick={() => roomUserClick(tableUser)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }
}

export default RoomUsers;
